// Full AI controller (P3): state machine driven by personality.behaviorBias.
//
// States: PATROL / PURSUE / ENGAGE / EVADE / ASSIST
// Transitions are gated by personality numbers so different personalities
// produce visibly different fighting styles (AC-13 quantitative differences):
//   pirate.attackRange = 30,   sniper.attackRange = 90  -> 30 < 90*0.5 = true.
//   maniac.recklessRam = 0.7   pushes ENGAGE through near-zero distance.
//   veteran.teammateProtect = 0.95 forces ASSIST when an ally is targeted.
//
// All math is local to plane orthonormal frame (forward / right / up).

import * as THREE from 'three';
import type { Plane } from '../plane/Plane';
import { type AIController, type AIWorldRef, blankInput } from './AIController';
import { type PersonalityId, getPersonality } from './personalities';
import type { InputState } from '../core/Input';

type State = 'patrol' | 'pursue' | 'engage' | 'evade' | 'assist';

export class FullAIController implements AIController {
  plane: Plane;
  personalityId: PersonalityId;
  callsign: string;

  state: State = 'patrol';
  target: Plane | null = null;
  protectedTeammate: Plane | null = null;
  private stateAge = 0;
  private targetAge = 0;
  private fireCooldownExtra = 0;
  private patrolHeading = new THREE.Vector3(0, 0, 1);

  constructor(plane: Plane, personalityId: PersonalityId, callsign: string) {
    this.plane = plane;
    this.personalityId = personalityId;
    this.callsign = callsign;
    // Initial patrol heading toward arena center.
    this.patrolHeading.copy(plane.forward);
  }

  update(dt: number, world: AIWorldRef): InputState {
    const out = blankInput();
    if (!this.plane.alive) return out;

    const p = getPersonality(this.personalityId).behavior;

    // ---------- target selection ----------
    this.targetAge += dt;
    const candidate = pickEnemy(this.plane, world.planes);
    if (candidate && (this.target == null || this.targetAge > 1.5 || !this.target.alive)) {
      this.target = candidate;
      this.targetAge = 0;
    }
    if (this.target && !this.target.alive) {
      this.target = candidate;
      this.targetAge = 0;
    }

    // ---------- state transitions ----------
    this.stateAge += dt;
    const tDist = this.target ? this.target.position.distanceTo(this.plane.position) : Infinity;
    const hpFrac = this.plane.hp / this.plane.maxHp;

    // Find a teammate in trouble for ASSIST (only veteran/rookie weight high).
    let needyAlly: Plane | null = null;
    let needyAllyEnemy: Plane | null = null;
    if (p.teammateProtect > 0.5) {
      for (const ally of world.planes) {
        if (!ally.alive || ally === this.plane || ally.team !== this.plane.team) continue;
        const enemy = closestEnemyOf(ally, world.planes);
        if (enemy) {
          const allyHpFrac = ally.hp / ally.maxHp;
          const enemyDist = enemy.position.distanceTo(ally.position);
          if (allyHpFrac < 0.7 && enemyDist < 60) {
            needyAlly = ally;
            needyAllyEnemy = enemy;
            break;
          }
        }
      }
    }

    let next: State = this.state;

    // EVADE has highest priority when low HP and aggression is moderate.
    const lowHpEvadeChance = (1 - hpFrac) * (1 - p.aggression);
    if (hpFrac <= 0.34 && lowHpEvadeChance > 0.3) {
      next = 'evade';
    } else if (needyAlly && needyAllyEnemy && p.teammateProtect > 0.6) {
      next = 'assist';
      this.protectedTeammate = needyAlly;
      this.target = needyAllyEnemy;
    } else if (this.target && tDist <= p.attackRange) {
      next = 'engage';
    } else if (this.target && tDist <= p.attackRange * 2.2) {
      next = 'pursue';
    } else {
      next = 'patrol';
    }

    if (next !== this.state) {
      this.state = next;
      this.stateAge = 0;
    }

    // ---------- per-state control ----------
    switch (this.state) {
      case 'patrol':
        this.controlPatrol(out, world, dt);
        break;
      case 'pursue':
        this.controlPursue(out, dt, p);
        break;
      case 'engage':
        this.controlEngage(out, dt, p);
        break;
      case 'evade':
        this.controlEvade(out, dt, p);
        break;
      case 'assist':
        this.controlAssist(out, dt, p);
        break;
    }

    if (this.fireCooldownExtra > 0) this.fireCooldownExtra -= dt;
    return out;
  }

  // ============== state implementations ==============

  private controlPatrol(out: InputState, world: AIWorldRef, dt: number): void {
    // Drift toward arena center, slight randomization on heading every 3s.
    if (this.stateAge < 0.05 || this.stateAge > 3) {
      const center = new THREE.Vector3(0, 30, 0);
      const toCenter = center.sub(this.plane.position).normalize();
      const jitter = new THREE.Vector3(
        (Math.random() - 0.5) * 0.6,
        (Math.random() - 0.5) * 0.3,
        (Math.random() - 0.5) * 0.6,
      );
      this.patrolHeading.copy(toCenter).add(jitter).normalize();
      this.stateAge = 0.05;
    }
    this.steerToward(this.patrolHeading, out, 0.7);
    out.throttle = 0.6;
    void dt; void world;
  }

  private controlPursue(out: InputState, dt: number, p: ReturnType<typeof getPersonality>['behavior']): void {
    if (!this.target) return;
    const desired = lead(this.plane, this.target, p.attackRange);
    this.steerToward(desired, out, 1.0);
    out.throttle = 0.95;
    out.boost = p.aggression > 0.6;
    void dt;
  }

  private controlEngage(out: InputState, dt: number, p: ReturnType<typeof getPersonality>['behavior']): void {
    if (!this.target) return;

    const toTarget = this.target.position.clone().sub(this.plane.position);
    const dist = toTarget.length();
    const dir = toTarget.normalize();

    const dotF = dir.dot(this.plane.forward);
    const dotR = dir.dot(this.plane.right);
    const dotU = dir.dot(this.plane.up);

    out.yaw = THREE.MathUtils.clamp(dotR * 1.6, -1, 1);
    out.pitch = THREE.MathUtils.clamp(dotU * 1.6, -1, 1);

    // Throttle: snipers slow down for accuracy, maniacs ram in.
    out.throttle = THREE.MathUtils.clamp(0.5 + p.aggression * 0.4 - p.sniperPatience * 0.3, 0.3, 1);

    // Reckless ram: if very close and personality bias is high, full throttle.
    if (p.recklessRam > 0.55 && dist < p.attackRange * 0.5) {
      out.throttle = 1;
      out.boost = true;
    }

    // Fire when generally aligned. Sniper requires tighter cone.
    const cosThreshold = p.sniperPatience > 0.7 ? 0.95 : 0.82;
    const inRange = dist >= p.attackRange * 0.3 && dist <= p.attackRange * 1.3;
    if (dotF > cosThreshold && inRange && this.fireCooldownExtra <= 0) {
      out.fire = true;
      out.firePulse = true;
      // Patient personalities pause longer between bursts.
      this.fireCooldownExtra = 0.18 + p.sniperPatience * 0.6 + Math.random() * 0.1;
    }
    void dt;
  }

  private controlEvade(out: InputState, dt: number, p: ReturnType<typeof getPersonality>['behavior']): void {
    // Hard turn away from the closest enemy, dive low, max throttle.
    if (!this.target) {
      out.throttle = 1;
      return;
    }
    const away = this.plane.position.clone().sub(this.target.position).normalize();
    away.y -= 0.3; // dive
    away.normalize();
    this.steerToward(away, out, 1.5);
    out.throttle = 1;
    out.boost = true;
    out.brake = false;
    void dt; void p;
  }

  private controlAssist(out: InputState, dt: number, p: ReturnType<typeof getPersonality>['behavior']): void {
    // Treat as pursue toward the target that's pestering the ally.
    this.controlPursue(out, dt, p);
  }

  // ============== helpers ==============

  private steerToward(worldDir: THREE.Vector3, out: InputState, gain = 1): void {
    const d = worldDir.clone().normalize();
    const dotR = d.dot(this.plane.right);
    const dotU = d.dot(this.plane.up);
    out.yaw = THREE.MathUtils.clamp(dotR * gain * 1.4, -1, 1);
    out.pitch = THREE.MathUtils.clamp(dotU * gain * 1.4, -1, 1);
  }
}

/** Pick the closest alive enemy of a given plane. */
function closestEnemyOf(self: Plane, planes: Plane[]): Plane | null {
  let best: Plane | null = null;
  let bestD = Infinity;
  for (const o of planes) {
    if (!o.alive || o === self || o.team === self.team) continue;
    const d = o.position.distanceToSquared(self.position);
    if (d < bestD) { bestD = d; best = o; }
  }
  return best;
}

/** Pick an enemy weighted by distance and how-much-on-our-front. */
function pickEnemy(self: Plane, planes: Plane[]): Plane | null {
  let best: Plane | null = null;
  let bestScore = -Infinity;
  for (const o of planes) {
    if (!o.alive || o === self || o.team === self.team) continue;
    const dir = o.position.clone().sub(self.position);
    const dist = dir.length();
    if (dist < 0.001) continue;
    const dotF = dir.divideScalar(dist).dot(self.forward);
    const score = -dist * 0.6 + dotF * 30;
    if (score > bestScore) { bestScore = score; best = o; }
  }
  return best;
}

/** Lead the target by predicting where they'll be when we close to attackRange. */
function lead(self: Plane, target: Plane, attackRange: number): THREE.Vector3 {
  const d = target.position.clone().sub(self.position);
  const dist = d.length();
  const closingSpeed = Math.max(8, self.aspects.topSpeed - target.velocity.dot(self.forward));
  const t = Math.min(2.5, Math.max(0, (dist - attackRange * 0.6) / closingSpeed));
  return target.position.clone().addScaledVector(target.velocity, t).sub(self.position).normalize();
}
